import { dataEvents } from "@/data/events/dataEvents";
import type { IBill } from "@/data/model/Bill";
import { UserItemSelectionChangeReason } from "@/data/model/Enums";
import type { BillItemSelectionChangedEvent } from "@/data/model/Events";

import { translations } from "@/shared/localization/translations";
import { toMoneyWithSign } from "@/shared/utils/format";

import { TastierClient } from "@/app/TastierClient";
import { BaseController } from "@/app/controller/BaseController";
import { PayButton } from "@/app/elements/PayButton";
import { trans } from "@/app/localization/Trans";
import { Helper } from "@/app/ui/Helper";
import { OrderSummary } from "@/app/ui/OrderSummary";
import { Popup } from "@/app/ui/Popup";
import { ServicesIcon } from "@/app/ui/ServicesIcon";
import { UserMenu } from "@/app/ui/UserMenu";

export class BillController extends BaseController {
    public onContinue?: { () };
    public onShowServices?: { () };

    private readonly $head: JQuery;
    private readonly $title: JQuery;

    private readonly $bottom: JQuery;
    private proceedBtn: PayButton;

    public userMenu: UserMenu;
    private orderSummary: OrderSummary;
    private $selectedLbl: JQuery;

    constructor() {
        super("bill");

        this.$head = $('<div class="head"></div>').appendTo(this.$view);
        this.$title = $('<div class="title">' + trans.bill.title + "</div>").appendTo(this.$head);

        new ServicesIcon().appendTo(this.$head).onClick = () => {
            if (this.onShowServices !== undefined) {
                this.onShowServices();
            }
        };

        this.userMenu = new UserMenu().appendTo(this.$head);
        this.orderSummary = new OrderSummary().appendTo(this.$view);
        this.$bottom = $('<div class="bottom" />').appendTo(this.$view);
        this.$selectedLbl = $('<div class="selected" />').appendTo(this.$bottom);

        this.proceedBtn = new PayButton(trans.general.continue)
            .setAmount(toMoneyWithSign(0, true))
            .setDisabled()
            .appendTo(this.$bottom);
        this.proceedBtn.onClick = () => {
            if (this.onContinue != null) {
                this.onContinue();
            }
        };

        TastierClient.context.onItemSelectionChange.add((a) => {
            this.orderSummary.updateSelection(a);
            this.updateBtn();
        }, this);

        TastierClient.context.onBillChange.add((a) => {
            this.redrawBill(a);
        });
    }

    public showSelectionChangedPopup() {
        Popup.show(
            translations.pay.failedBillInvalidSelection,
            undefined,
            translations.general.ok,
            undefined,
            "pmtconfirm"
        );
    }

    protected onBecomeVisible() {
        const serviceDesignation = Helper.getCurrentServiceDesignation();
        this.$title.text(serviceDesignation === undefined ? trans.order.title : serviceDesignation);
        this.redrawBill(TastierClient.context.currentBill);

        dataEvents.appSocket.ItemSelectionChanged.add(this.handleItemSelectionChanged, this);
    }

    protected onBecomeInvisible() {
        dataEvents.appSocket.ItemSelectionChanged.remove(this.handleItemSelectionChanged, this);
    }

    private redrawBill(bill?: IBill) {
        this.orderSummary.reset();
        this.orderSummary.updateBill(bill);

        if (bill === undefined) {
            this.proceedBtn.setDisabled(trans.bill.waitingForOrder);
        }

        this.updateBtn();
    }

    private updateBtn() {
        const selectedItemCount = TastierClient.context.getSelectedItemCount();
        const payable = TastierClient.context.getBillPayableAmount();
        const isFullyPaid = TastierClient.context.isBillFullyPaid();

        if (isFullyPaid) {
            this.$selectedLbl.html(trans.bill.fullyPaid);
        } else {
            this.$selectedLbl.html(TastierClient.context.getSelectedItemCount() + " " + trans.bill.selectedItems);
        }

        this.proceedBtn.setAmount(toMoneyWithSign(payable, true));
        if (!isFullyPaid && selectedItemCount > 0) {
            this.proceedBtn.setEnabled();
        } else {
            this.proceedBtn.setDisabled(toMoneyWithSign(0, true));
        }
    }

    private handleItemSelectionChanged(eventData: BillItemSelectionChangedEvent) {
        if (eventData.identityId !== TastierClient.identityContext.getIdentityId()) {
            return;
        }

        if (eventData.reason == UserItemSelectionChangeReason.SelectionsReset) {
            this.showSelectionChangedPopup();
        }
    }
}
